import React, { useState } from 'react'
import { Navigate, useLocation, Link } from 'react-router-dom'
import { ShieldOff } from 'lucide-react'
import { usePermission, permissionForPath } from '../../hooks/usePermission'
import Sidebar from './Sidebar'
import Topbar from './Topbar'
import Toast from '../shared/Toast'
import { useAuth } from '../../hooks/useAuth'
import { Spinner } from '../shared/LoadingState'
import { tx } from '../../lib/i18n'

interface MainLayoutProps {
  children: React.ReactNode
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isAuthenticated, isLoading } = useAuth()
  const { can } = usePermission()
  const location = useLocation()
  const needed = permissionForPath(location.pathname)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p className="text-sm text-gray-400">{tx('Loading Queza Admin...')}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar onMenuClick={() => setMobileMenuOpen(true)} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {needed && !can(needed) ? (
            <div className="max-w-md mx-auto mt-16 bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm" data-testid="no-permission">
              <ShieldOff className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h1 className="text-lg font-semibold text-gray-900">{tx('No access to this page')}</h1>
              <p className="text-sm text-gray-500 mt-1">{tx('Ask an admin who manages permissions to grant it.')}</p>
              <Link to="/dashboard" className="inline-block mt-5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                {tx('Back to dashboard')}
              </Link>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
      <Toast />
    </div>
  )
}

export default MainLayout
